// src/components/public/ProfessionalPublicProfile.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Avatar,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  useMediaQuery,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Calendar,
  Clock,
  Award,
  Briefcase,
  CheckCircle,
  X,
  MapPin,
  Star,
  ArrowRight,
  BadgeCheck,
  Home,
  Video,
  Tag
} from 'lucide-react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import es from 'date-fns/locale/es';
import { motion } from 'framer-motion';
import myAGTService from '../../services/myAGTService';
import StoriesCircle from './StoriesCircle';
import StoryViewer from './StoryViewer';

// Colores y estilos constantes
const HEALTH_GREEN = '#4CAF50';
const GREEN_GRADIENT_START = '#4CAF50';
const GREEN_GRADIENT_MIDDLE = '#3D8B40';
const GREEN_GRADIENT_END = '#2E7031';

const ProfessionalPublicProfile = () => {
  const { slug } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  const datePickerRef = useRef(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [patientData, setPatientData] = useState({
    name: '',
    lastName: '', 
    email: '',
    phone: '',
    identificationNumber: '', 
    countryCode: 'cl', // Preseleccionado en Chile
    reason: ''
  });
  const [appointmentData, setAppointmentData] = useState({
    appointmentMode: null, // 'inPerson', 'homeVisit', 'online'
    homeAddress: '',
    selectedService: null // Nuevo campo para el servicio seleccionado
  });
  const [bookingStatus, setBookingStatus] = useState({
    loading: false,
    success: false,
    error: null,
    data: null
  });
  
  // Estado para rastrear el envío de WhatsApp
  const [whatsappStatus, setWhatsappStatus] = useState({
    sent: false,
    verified: false,
    checking: false
  });
  
  // Estados para historias
  const [stories, setStories] = useState([]);
  const [loadingStories, setLoadingStories] = useState(false);
  const [storyViewerOpen, setStoryViewerOpen] = useState(false);
  
  // Estado para modalidad de atención
  const [selectedMode, setSelectedMode] = useState(null);

  // Estado para servicios disponibles
  const [availableServices, setAvailableServices] = useState([]);
  
  // Obtener la zona horaria local del usuario
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Cargar perfil del profesional al iniciar
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
  
      try {
        const response = await myAGTService.getPublicProfile(slug);
        console.log("Datos recibidos del backend:", response);
        
        if (response.success && response.data) {
          setProfile(response.data);
  
          // Filtrar servicios activos para mostrar solo los disponibles
          const activeServices = response.data.professionalServices?.filter(service => service.isActive) || [];
          setAvailableServices(activeServices);
          console.log("Servicios disponibles:", activeServices);
          
          // Cargar historias inmediatamente después de cargar el perfil
          fetchStories();
        } else {
          throw new Error('No se pudo cargar el perfil');
        }
      } catch (err) {
        console.error('Error al cargar perfil:', err);
        setError('No se pudo encontrar el perfil solicitado');
      } finally {
        setLoading(false);
      }
    };
  
    if (slug) {
      fetchProfile();
    }
  }, [slug]);

  // Función para cargar historias
  const fetchStories = async () => {
    if (!slug) return;
  
    setLoadingStories(true);
    try {
      console.log('Obteniendo historias públicas para:', slug);
      const response = await myAGTService.getPublicStories(slug);
      console.log('Respuesta de historias públicas:', response);
      if (response.success) {
        setStories(response.data || []);
      }
    } catch (error) {
      console.error('Error al cargar historias:', error);
    } finally {
      setLoadingStories(false);
    }
  };

  // Calcular slots disponibles cuando cambia la fecha
  useEffect(() => {
    if (!selectedDate || !profile?.availability) return;
    
    const calculateAvailableSlots = async () => {
      const { workDays, workHours, slotDuration, exceptions } = profile.availability;
      const dayOfWeek = selectedDate.getDay(); // 0 = Domingo, 1 = Lunes, etc.
      
      // Verificar si es un día laborable
      if (!workDays.includes(dayOfWeek)) {
        setAvailableSlots([]);
        return;
      }
      
      // Convertir fecha a formato YYYY-MM-DD
      const dateStr = selectedDate.toISOString().split('T')[0];
      
      // Buscar si hay excepción para esta fecha
      const exception = exceptions.find(exc => exc.date === dateStr);
      
      // Si la fecha está bloqueada, no hay slots disponibles
      if (exception?.isBlocked) {
        setAvailableSlots([]);
        return;
      }
      
      // Determinar horas de inicio y fin
      const startHour = exception?.hours?.start || workHours.start;
      const endHour = exception?.hours?.end || workHours.end;
      
      // Convertir horas a minutos desde medianoche
      const startMinutes = convertTimeToMinutes(startHour);
      const endMinutes = convertTimeToMinutes(endHour);
      
      // Generar slots
      const slots = [];
      for (let time = startMinutes; time < endMinutes; time += slotDuration) {
        slots.push({
          time: formatMinutesToTime(time),
          available: true // Por defecto disponible, luego verificamos con las citas existentes
        });
      }
      
      // Filtrar slots pasados si es el día actual
      const now = new Date();
      const isToday = selectedDate.toDateString() === now.toDateString();
      
      let filteredSlots = slots;
      
      if (isToday) {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        const currentTimeInMinutes = currentHour * 60 + currentMinute;
        
        // Añadir un margen de tiempo (por ejemplo, 30 minutos)
        const timeMargin = 30; 
        const cutoffTimeInMinutes = currentTimeInMinutes + timeMargin;
        
        // Filtrar solo slots futuros con el margen de tiempo
        filteredSlots = slots.filter(slot => {
          const slotMinutes = convertTimeToMinutes(slot.time);
          return slotMinutes > cutoffTimeInMinutes;
        });
      }
      
      // Verificar disponibilidad real de cada slot consultando al backend
      const availabilityChecks = await Promise.all(
        filteredSlots.map(async (slot) => {
          try {
            // Verificar disponibilidad real en el backend
            const isAvailable = await myAGTService.checkSlotAvailability(slug, {
              date: dateStr,
              time: slot.time,
              timezone: userTimezone
            });
            
            return {
              ...slot,
              available: isAvailable
            };
          } catch (error) {
            console.error(`Error verificando disponibilidad para ${slot.time}:`, error);
            return {
              ...slot,
              available: false // Si hay error, marcar como no disponible por precaución
            };
          }
        })
      );
      
      setAvailableSlots(availabilityChecks);
    };
    
    calculateAvailableSlots();
  }, [selectedDate, profile, slug, userTimezone]);

  // Función para refrescar los slots disponibles
  const refreshAvailableSlots = async () => {
    if (!selectedDate || !profile?.availability) return;
    
    // Volver a calcular los slots disponibles
    const { workDays, workHours, slotDuration, exceptions } = profile.availability;
    const dayOfWeek = selectedDate.getDay();
    
    // Verificar si es un día laborable
    if (!workDays.includes(dayOfWeek)) {
      setAvailableSlots([]);
      return;
    }
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    const exception = exceptions.find(exc => exc.date === dateStr);
    
    if (exception?.isBlocked) {
      setAvailableSlots([]);
      return;
    }
    
    const startHour = exception?.hours?.start || workHours.start;
    const endHour = exception?.hours?.end || workHours.end;
    const startMinutes = convertTimeToMinutes(startHour);
    const endMinutes = convertTimeToMinutes(endHour);
    
    // Generar slots
    const slots = [];
    for (let time = startMinutes; time < endMinutes; time += slotDuration) {
      slots.push({
        time: formatMinutesToTime(time),
        available: true
      });
    }
    
    // Filtrar slots pasados si es el día actual
    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();
    
    let filteredSlots = slots;
    
    if (isToday) {
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      const timeMargin = 30;
      const cutoffTimeInMinutes = currentTimeInMinutes + timeMargin;
      
      filteredSlots = slots.filter(slot => {
        const slotMinutes = convertTimeToMinutes(slot.time);
        return slotMinutes > cutoffTimeInMinutes;
      });
    }
    
    // Verificar disponibilidad real de cada slot
    const availabilityChecks = await Promise.all(
      filteredSlots.map(async (slot) => {
        try {
          const isAvailable = await myAGTService.checkSlotAvailability(slug, {
            date: dateStr,
            time: slot.time,
            timezone: userTimezone
          });
          
          return {
            ...slot,
            available: isAvailable
          };
        } catch (error) {
          console.error(`Error verificando disponibilidad para ${slot.time}:`, error);
          return {
            ...slot,
            available: false
          };
        }
      })
    );
    
    setAvailableSlots(availabilityChecks);
  };

  // Convertir hora (HH:MM) a minutos desde medianoche
  const convertTimeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Convertir minutos desde medianoche a hora (HH:MM)
  const formatMinutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Formatear hora para mostrar en formato local (12h)
  const formatTimeDisplay = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true // Usar formato 12h (AM/PM)
    });
  };

  // Seleccionar slot y abrir diálogo de reserva
  const handleSelectSlot = (slot) => {
    // Obtener la fecha local del usuario
    const localDate = selectedDate.toISOString().split('T')[0];
    
    setError(null);
    
    // Bloquear temporalmente el slot
    myAGTService.blockTimeSlot(slug, {
      date: localDate,
      time: slot.time,
      timezone: userTimezone
    }).then((response) => {
      if (response.success) {
        setSelectedSlot({
          ...slot,
          lockId: response.lockId // Guardar el ID del bloqueo
        });
        setBookingDialogOpen(true);
        setActiveStep(0);
        setAppointmentData({
          appointmentMode: null,
          homeAddress: '',
          selectedService: null
        });
        setPatientData({
          name: '',
          lastName: '',
          email: '',
          phone: '',
          identificationNumber: '',
          countryCode: 'cl', // Preseleccionar Chile
          reason: ''
        });
        setBookingStatus({
          loading: false,
          success: false,
          error: null,
          data: null
        });
        setWhatsappStatus({
          sent: false,
          verified: false,
          checking: false
        });
      } else {
        throw new Error(response.message || 'No se pudo reservar este horario');
      }
    }).catch(err => {
      console.error('Error al bloquear slot:', err);
      setError('No se pudo reservar este horario. Por favor, intenta con otro.');
      
      // Actualizar la lista de slots disponibles
      if (selectedDate) {
        refreshAvailableSlots();
      }
    });
  };

  // Cerrar diálogo de reserva
  const handleCloseBookingDialog = () => {
    // Solo permitir cerrar si no está en proceso de reserva
    if (!bookingStatus.loading) {
      // Si hay un slot seleccionado con lockId y no se completó la reserva, liberar el bloqueo
      if (selectedSlot?.lockId && activeStep < 2) {
        myAGTService.releaseTimeSlot(slug, selectedSlot.lockId)
          .catch(err => console.error('Error al liberar bloqueo:', err));
      }
      
      setBookingDialogOpen(false);
      
      // Refrescar slots disponibles al cerrar
      refreshAvailableSlots();
    }
  };

  // Manejar cambio en datos del paciente
  const handlePatientDataChange = (e) => {
    const { name, value } = e.target;
    setPatientData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Avanzar al siguiente paso
  const handleNext = () => {
    if (activeStep === 0 && availableServices.length > 0) {
      // Si hay servicios disponibles y son compatibles con la modalidad seleccionada, 
      // ir al paso de selección de servicio
      const servicesForSelectedMode = availableServices.filter(service => 
        service.supportedModes && service.supportedModes[appointmentData.appointmentMode]
      );
      
      if (servicesForSelectedMode.length > 0) {
        setActiveStep(0.5);
      } else {
        // Si no hay servicios compatibles, continuar al siguiente paso
        setActiveStep(1);
      }
    } else if (activeStep === 0.5) {
      // Desde el paso de selección de servicio, ir al paso de datos personales
      setActiveStep(1);
    } else {
      // En otros casos, simplemente avanzar al siguiente paso entero
      setActiveStep(Math.floor(activeStep) + 1);
    }
  };

  // Retroceder al paso anterior
  const handleBack = () => {
    if (activeStep === 1 && availableServices.length > 0) {
      // Si estamos en el paso 1 y hay servicios disponibles, volver al paso de selección de servicio
      setActiveStep(0.5);
    } else if (activeStep === 0.5) {
      // Si estamos en el paso de selección de servicio, volver al paso de modalidad
      setActiveStep(0);
    } else {
      // En otros casos, simplemente retroceder al paso anterior entero
      setActiveStep(Math.floor(activeStep) - 1);
    }
  };

  // Manejar selección de servicio
  const handleServiceSelection = (service) => {
    setAppointmentData(prev => ({
      ...prev,
      selectedService: service
    }));
  };

  // Confirmar reserva
  const handleConfirmBooking = async () => {
    setBookingStatus({
      loading: true,
      success: false,
      error: null,
      data: null
    });
    
    try {
      const bookingRequestData = {
        date: selectedDate.toISOString().split('T')[0],
        time: selectedSlot.time,
        timezone: userTimezone,
        lockId: selectedSlot.lockId, // Incluir el ID del bloqueo
        patientName: patientData.name,
        patientLastName: patientData.lastName,
        patientEmail: patientData.email,
        patientPhone: patientData.phone,
        patientIdentificationNumber: patientData.identificationNumber,
        patientCountryCode: patientData.countryCode,
        reason: patientData.reason,
        // Información de modalidad de atención
        appointmentMode: appointmentData.appointmentMode,
        homeAddress: appointmentData.appointmentMode === 'homeVisit' ? appointmentData.homeAddress : null,
        // Información del servicio seleccionado
        serviceId: appointmentData.selectedService?.id || null,
        serviceName: appointmentData.selectedService?.name || 'Consulta general',
        servicePrice: appointmentData.selectedService?.price || null
      };
      
      const response = await myAGTService.bookAppointment(slug, bookingRequestData);
      
      if (response.success) {
        setBookingStatus({
          loading: false,
          success: true,
          error: null,
          data: response.data
        });
        
        // Establecer estado inicial de WhatsApp según respuesta del servidor
        setWhatsappStatus({
          sent: response.data.whatsappSent || false,
          verified: false,
          checking: false
        });
        
        // Avanzar al paso de confirmación
        setActiveStep(3);
        
        // Refrescar slots disponibles después de reservar
        refreshAvailableSlots();
      } else {
        throw new Error(response.message || 'Error al reservar cita');
      }
    } catch (err) {
      console.error('Error al reservar cita:', err);
      setBookingStatus({
        loading: false,
        success: false,
        error: err.message || 'Error al reservar cita. Por favor, intenta nuevamente.',
        data: null
      });
      
      // Si el error es por disponibilidad, cerrar el diálogo y actualizar slots
      if (err.message && err.message.includes('disponible')) {
        setTimeout(() => {
          setBookingDialogOpen(false);
          refreshAvailableSlots();
        }, 3000);
      }
    }
  };

  // Verificar estado del mensaje de WhatsApp
  const verifyWhatsAppDelivery = async (messageId) => {
    if (!messageId) {
      return false;
    }
    
    setWhatsappStatus(prev => ({ ...prev, checking: true }));
    
    try {
      // Llamada al backend para verificar el estado del mensaje
      const response = await myAGTService.checkWhatsAppMessageStatus(messageId);
      
      const isDelivered = response.status === 'sent' || 
                          response.status === 'delivered' || 
                          response.status === 'read';
      
      setWhatsappStatus({
        sent: isDelivered,
        verified: true,
        checking: false
      });
      
      return isDelivered;
    } catch (error) {
      console.error('Error al verificar estado del mensaje:', error);
      setWhatsappStatus({
        sent: false,
        verified: true,
        checking: false
      });
      return false;
    }
  };

  // Verificar estado del mensaje cuando se completa la reserva
  useEffect(() => {
    if (activeStep === 2 && bookingStatus.success) {
      setWhatsappStatus(prev => ({
        ...prev,
        sent: bookingStatus.data?.whatsappSent || false
      }));
      
      // Si tenemos ID de mensaje, verificar su estado después de unos segundos
      if (bookingStatus.data?.whatsappMessageId) {
        const timer = setTimeout(() => {
          verifyWhatsAppDelivery(bookingStatus.data.whatsappMessageId);
        }, 3000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [activeStep, bookingStatus.success, bookingStatus.data]);

  // Función para abrir WhatsApp con mensaje predefinido
  const openWhatsAppFallback = () => {
    const message = encodeURIComponent(
      `Hola, soy ${patientData.name} ${patientData.lastName} y acabo de agendar una cita con ${profile.name} para el ${selectedDate?.toLocaleDateString()} a las ${formatTimeDisplay(selectedSlot?.time)}. Quisiera recibir la confirmación de mi cita por este medio.`
    );
    window.open(`https://wa.me/19032063953?text=${message}`, '_blank');
  };

  // Función para solicitar reenvío de confirmación
  const requestConfirmationResend = async () => {
    if (!bookingStatus.data?.appointmentId || !patientData.phone) {
      return;
    }
    
    setWhatsappStatus(prev => ({ ...prev, checking: true }));
    
    try {
      await myAGTService.resendAppointmentConfirmation(
        bookingStatus.data.appointmentId,
        patientData.phone
      );
      
      // Establecer estado como enviado
      setWhatsappStatus({
        sent: true,
        verified: true,
        checking: false
      });
    } catch (error) {
      console.error('Error al reenviar confirmación:', error);
      setWhatsappStatus(prev => ({ 
        ...prev, 
        checking: false 
      }));
      
      // Mostrar mensaje de error
      alert('No pudimos reenviar la confirmación. Por favor, intenta usar el botón de WhatsApp.');
    }
  };

  // Componente de encabezado con logo
  const Header = () => (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        py: 2,
        mb: 2
      }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          sx={{ 
            fontWeight: 600, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          AGT Health
          <motion.span
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5, type: "spring" }}
            style={{ 
              color: HEALTH_GREEN, 
              fontSize: isMobile ? '20px' : '28px', 
              fontWeight: 700, 
              marginLeft: '2px',
              position: 'relative',
              top: '-8px'
            }}
          >
            +
          </motion.span>
        </Typography>
      </motion.div>
    </Box>
  );

  // Componente de footer actualizado con enlaces funcionales
  const Footer = () => (
    <Box
      sx={{
        py: 4,
        mt: 6,
        backgroundColor: 'rgba(0,0,0,0.02)',
        borderTop: '1px solid rgba(0,0,0,0.05)',
        display: 'flex',
        justifyContent: 'center'
      }}
    >
      <Container maxWidth="sm">
        <Grid container spacing={4}>
          <Grid item xs={12} md={6} sx={{ textAlign: 'left' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                mb: 2, 
                display: 'flex', 
                alignItems: 'center' 
              }}
            >
              AGT Health
              <span style={{ 
                color: HEALTH_GREEN, 
                fontSize: '16px', 
                fontWeight: 700, 
                marginLeft: '2px',
                position: 'relative',
                top: '-4px'
              }}>
                +
              </span>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              AGT Health is part of AGT developed by DirectiveAI. AGT and related technologies are designed to enhance and augment human capabilities, not replace them.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Using AGT products requires acceptance of our AI ethical guidelines and responsible use policies.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: 'left' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Subscription required for AGT Health for Professionals. AGT Health is free to use within the AGT ecosystem. Learn more at {" "}
              <Link to="https://agtapp.com" target="_blank" style={{ color: HEALTH_GREEN }}>
                agtapp.com
              </Link> or {" "}
              <Link to="https://agtapp.com" target="_blank" style={{ color: HEALTH_GREEN }}>
                click here
              </Link>. API usage limits and fair use policies apply.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              DirectiveAI is committed to responsible AI development and environmental sustainability. {" "}
              <Link to="https://directiveai.org/" target="_blank" style={{ color: HEALTH_GREEN }}>
                Learn more
              </Link> about our environmental and ethical commitments.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              © {new Date().getFullYear()} DirectiveAI. All rights reserved.
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  // Componente de enlaces para profesionales y centros médicos con bordes más redondeados
  const QuickLinks = () => (
    <Box sx={{ textAlign: 'center', mt: 4, mb: 6 }}>
      <Divider sx={{ mb: 4 }} />
      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} sm={6} md={5}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              borderRadius: 4, // Aumentado el radio para hacerlo más redondeado
              backgroundColor: 'rgba(0,0,0,0.02)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
              }
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}> {/* Aumentado el grosor del título */}
              ¿Eres profesional de la salud?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Únete a nuestra plataforma y amplía tu alcance profesional
            </Typography>
            <Button 
              component={Link} 
              to="https://health.agtapp.com"
              target="_blank"
              variant="contained"
              disableElevation // Elimina la sombra
              sx={{ 
                mt: 1,
                borderRadius: 28,
                textTransform: 'none',
                px: 3,
                background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                color: '#fff',
                '&:hover': {
                  background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                  boxShadow: 'none' // Asegura que no haya sombra al pasar el mouse
                },
                boxShadow: 'none' // Elimina cualquier sombra predeterminada
              }}
              endIcon={<ArrowRight size={16} />}
            >
              Registrate aquí
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={5}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              borderRadius: 4, // Aumentado el radio para hacerlo más redondeado
              backgroundColor: 'rgba(0,0,0,0.02)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
              }
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}> {/* Aumentado el grosor del título */}
              ¿Buscas tu centro médico?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Encuentra centros médicos cercanos de nuestra red
            </Typography>
            <Button 
              component={Link} 
              to="https://agtapp.com"
              target="_blank"
              variant="contained"
              disableElevation // Elimina la sombra
              sx={{ 
                mt: 1,
                borderRadius: 28,
                textTransform: 'none',
                px: 3,
                background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                color: '#fff',
                '&:hover': {
                  background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                  boxShadow: 'none' // Asegura que no haya sombra al pasar el mouse
                },
                boxShadow: 'none' // Elimina cualquier sombra predeterminada
              }}
              endIcon={<ArrowRight size={16} />}
            >
              Encuéntralo aquí
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <CircularProgress sx={{ color: GREEN_GRADIENT_START }} />
          </Box>
        </Container>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Box>
        </Container>
        <Footer />
      </>
    );
  }

  if (!profile) {
    return (
      <>
        <Header />
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Alert severity="warning" sx={{ width: '100%' }}>
              El perfil solicitado no existe o no está disponible.
            </Alert>
          </Box>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="md" sx={{ pb: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header con información del profesional */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 4 }, 
              mb: 4, 
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(0,0,0,0.05)'
            }}
          >
            <Grid container spacing={isMobile ? 2 : 4}>
              <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Avatar
                    src={profile.profileImageUrl}
                    sx={{ 
                      width: { xs: 100, sm: 120 }, 
                      height: { xs: 100, sm: 120 }
                    }}
                    alt={profile.name}
                  />
                </motion.div>
              </Grid>
              
              <Grid item xs={12} sm={8} md={9}>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center' }}
                  >
                    {profile.name}
                    <BadgeCheck size={24} style={{ marginLeft: 8, color: HEALTH_GREEN }} />
                  </Typography>
                  
                  <Typography 
                    variant="h6" 
                    sx={{ mb: 2, color: GREEN_GRADIENT_START, fontWeight: 500 }}
                  >
                    {profile.title}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {profile.specialties.map((specialty, index) => (
                      <Chip 
                        key={index} 
                        label={specialty} 
                        size="small" 
                        sx={{ 
                          borderRadius: 6, 
                          bgcolor: 'rgba(76,175,80,0.1)', 
                          color: GREEN_GRADIENT_START,
                          fontWeight: 500
                        }} 
                      />
                    ))}
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                    {profile.yearsExperience && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Briefcase size={18} style={{ marginRight: 8, color: GREEN_GRADIENT_START }} />
                        <Typography variant="body2">
                          {profile.yearsExperience} años de experiencia
                        </Typography>
                      </Box>
                    )}
                    
                    {profile.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MapPin size={18} style={{ marginRight: 8, color: GREEN_GRADIENT_START }} />
                        <Typography variant="body2">
                          {profile.location}
                        </Typography>
                      </Box>
                    )}
                    
                    {profile.rating && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Star size={18} style={{ marginRight: 8, color: GREEN_GRADIENT_START }} />
                        <Typography variant="body2">
                          {profile.rating} ({profile.reviewCount || 0} reseñas)
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
            
            {/* Sección de historias */}
            {stories.length > 0 && (
              <Box sx={{ 
                mt: 3, 
                display: 'flex',
                alignItems: 'center',
                overflowX: 'auto',
                pb: 1,
                '&::-webkit-scrollbar': {
                  height: 6
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: 3
                }
              }}>
            
                {loadingStories ? (
                  // Mostrar skeletons mientras carga
                  Array(3).fill().map((_, index) => (
                    <StoriesCircle key={index} loading={true} />
                  ))
                ) : (
                  // Mostrar historias
                  stories.map((story) => (
                    <StoriesCircle
                      key={story.storyId}
                      image={story.imageUrl}
                      name={new Date(story.createdAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      hasStories={true}
                      isViewed={false}
                      size="small"
                      onClick={() => setStoryViewerOpen(true)}
                    />
                  ))
                )}
              </Box>
            )}
            
            {/* Mostrar modalidades de atención disponibles */}
            {(profile.appointmentModes?.inPerson || 
              profile.appointmentModes?.homeVisit || 
              profile.appointmentModes?.online) && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Modalidades de atención
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {profile.appointmentModes?.inPerson && (
                    <Box
                      onClick={() => setSelectedMode('inPerson')}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 20,
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        background: selectedMode === 'inPerson' 
                          ? `linear-gradient(135deg, ${GREEN_GRADIENT_START}12 0%, ${GREEN_GRADIENT_MIDDLE}08 100%)`
                          : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: selectedMode === 'inPerson' 
                          ? `0 4px 12px rgba(76, 175, 80, 0.15)`
                          : '0 2px 8px rgba(0, 0, 0, 0.04)',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          background: selectedMode === 'inPerson' 
                            ? `linear-gradient(135deg, ${GREEN_GRADIENT_START}18 0%, ${GREEN_GRADIENT_MIDDLE}12 100%)`
                            : 'rgba(248, 248, 248, 0.9)',
                          boxShadow: selectedMode === 'inPerson' 
                            ? `0 6px 16px rgba(76, 175, 80, 0.2)`
                            : '0 4px 12px rgba(0, 0, 0, 0.08)',
                        }
                      }}
                    >
                      <MapPin 
                        size={16} 
                        style={{ 
                          color: selectedMode === 'inPerson' ? GREEN_GRADIENT_MIDDLE : 'rgba(0, 0, 0, 0.6)' 
                        }} 
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          letterSpacing: '0.01em',
                          color: selectedMode === 'inPerson' ? GREEN_GRADIENT_END : 'rgba(0, 0, 0, 0.7)',
                        }}
                      >
                        Presencial
                      </Typography>
                    </Box>
                  )}
                  
                  {profile.appointmentModes?.homeVisit && (
                    <Box
                      onClick={() => setSelectedMode('homeVisit')}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 20,
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        background: selectedMode === 'homeVisit' 
                          ? `linear-gradient(135deg, ${GREEN_GRADIENT_START}12 0%, ${GREEN_GRADIENT_MIDDLE}08 100%)`
                          : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: selectedMode === 'homeVisit' 
                          ? `0 4px 12px rgba(76, 175, 80, 0.15)`
                          : '0 2px 8px rgba(0, 0, 0, 0.04)',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          background: selectedMode === 'homeVisit' 
                            ? `linear-gradient(135deg, ${GREEN_GRADIENT_START}18 0%, ${GREEN_GRADIENT_MIDDLE}12 100%)`
                            : 'rgba(248, 248, 248, 0.9)',
                          boxShadow: selectedMode === 'homeVisit' 
                            ? `0 6px 16px rgba(76, 175, 80, 0.2)`
                            : '0 4px 12px rgba(0, 0, 0, 0.08)',
                        }
                      }}
                    >
                      <Home 
                        size={16} 
                        style={{ 
                          color: selectedMode === 'homeVisit' ? GREEN_GRADIENT_MIDDLE : 'rgba(0, 0, 0, 0.6)' 
                        }} 
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          letterSpacing: '0.01em',
                          color: selectedMode === 'homeVisit' ? GREEN_GRADIENT_END : 'rgba(0, 0, 0, 0.7)',
                        }}
                      >
                        A domicilio
                      </Typography>
                    </Box>
                  )}
                  
                  {profile.appointmentModes?.online && (
                    <Box
                      onClick={() => setSelectedMode('online')}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 20,
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        background: selectedMode === 'online' 
                          ? `linear-gradient(135deg, ${GREEN_GRADIENT_START}12 0%, ${GREEN_GRADIENT_MIDDLE}08 100%)`
                          : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: selectedMode === 'online' 
                          ? `0 4px 12px rgba(76, 175, 80, 0.15)`
                          : '0 2px 8px rgba(0, 0, 0, 0.04)',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          background: selectedMode === 'online' 
                            ? `linear-gradient(135deg, ${GREEN_GRADIENT_START}18 0%, ${GREEN_GRADIENT_MIDDLE}12 100%)`
                            : 'rgba(248, 248, 248, 0.9)',
                          boxShadow: selectedMode === 'online' 
                            ? `0 6px 16px rgba(76, 175, 80, 0.2)`
                            : '0 4px 12px rgba(0, 0, 0, 0.08)',
                        }
                      }}
                    >
                      <Video 
                        size={16} 
                        style={{ 
                          color: selectedMode === 'online' ? GREEN_GRADIENT_MIDDLE : 'rgba(0, 0, 0, 0.6)' 
                        }} 
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          letterSpacing: '0.01em',
                          color: selectedMode === 'online' ? GREEN_GRADIENT_END : 'rgba(0, 0, 0, 0.7)',
                        }}
                      >
                        Online
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Mostrar información adicional según la modalidad seleccionada */}
                {selectedMode === 'inPerson' && profile.appointmentLocations?.inPersonAddress && (
                  <Box 
                    sx={{ 
                      mt: 3, 
                      p: 3, 
                      background: 'rgba(255, 255, 255, 0.6)',
                      backdropFilter: 'blur(20px) saturate(180%)',
                      border: '1px solid rgba(0, 0, 0, 0.06)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 2,
                        fontSize: '0.9rem',
                        letterSpacing: '-0.01em',
                        color: 'rgba(0, 0, 0, 0.85)'
                      }}
                    >
                      {profile.appointmentLocations.inPersonAddress}
                    </Typography>
                    {profile.appointmentLocations.inPersonMapLink && (
                      <Link 
                        href={profile.appointmentLocations.inPersonMapLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        sx={{ 
                          display: 'inline-flex', 
                          alignItems: 'center',
                          gap: 1,
                          color: GREEN_GRADIENT_START, 
                          mb: 1.5,
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          padding: '8px 16px',
                          borderRadius: 20,
                          background: 'rgba(76, 175, 80, 0.08)',
                          border: '1px solid rgba(76, 175, 80, 0.12)',
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            background: 'rgba(76, 175, 80, 0.12)',
                            transform: 'translateY(-1px)',
                            textDecoration: 'none'
                          }
                        }}
                      >
                        <MapPin size={14} />
                        Ver ubicación en mapa
                      </Link>
                    )}
                    {profile.appointmentLocations.inPersonInstructions && (
                      <Typography 
                        variant="body2" 
                        sx={{
                          color: 'rgba(0, 0, 0, 0.6)',
                          lineHeight: 1.5,
                          fontSize: '0.875rem',
                          letterSpacing: '0.01em'
                        }}
                      >
                        {profile.appointmentLocations.inPersonInstructions}
                      </Typography>
                    )}
                  </Box>
                )}

                {selectedMode === 'homeVisit' && (
                  <Box 
                    sx={{ 
                      mt: 3, 
                      p: 3, 
                      background: 'rgba(255, 255, 255, 0.6)',
                      backdropFilter: 'blur(20px) saturate(180%)',
                      border: '1px solid rgba(0, 0, 0, 0.06)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        lineHeight: 1.5,
                        fontSize: '0.875rem',
                        letterSpacing: '0.01em'
                      }}
                    >
                      El profesional se desplazará a tu domicilio. Deberás proporcionar tu dirección al confirmar la cita.
                    </Typography>
                  </Box>
                )}

                {selectedMode === 'online' && (
                  <Box 
                    sx={{ 
                      mt: 3, 
                      p: 3, 
                      background: 'rgba(255, 255, 255, 0.6)',
                      backdropFilter: 'blur(20px) saturate(180%)',
                      border: '1px solid rgba(0, 0, 0, 0.06)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1.5,
                        fontSize: '0.9rem',
                        letterSpacing: '-0.01em',
                        color: 'rgba(0, 0, 0, 0.85)'
                      }}
                    >
                      Plataforma: {
                        profile.appointmentLocations?.onlinePlatform === 'zoom' ? 'Zoom' :
                        profile.appointmentLocations?.onlinePlatform === 'meet' ? 'Google Meet' :
                        profile.appointmentLocations?.onlinePlatform === 'teams' ? 'Microsoft Teams' :
                        'Plataforma personalizada'
                      }
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        lineHeight: 1.5,
                        fontSize: '0.875rem',
                        letterSpacing: '0.01em'
                      }}
                    >
                      Recibirás un enlace para la videoconsulta al confirmar la cita.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {/* Mostrar servicios profesionales disponibles */}
            {profile.professionalServices && profile.professionalServices.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Servicios profesionales
                </Typography>
                
                <Grid container spacing={2}>
                  {profile.professionalServices.map((service) => (
                    <Grid item xs={12} sm={6} md={4} key={service.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 0,
                          borderRadius: 3.5,
                          background: 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(20px) saturate(180%)',
                          border: '1px solid rgba(0, 0, 0, 0.08)',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.08)',
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            background: 'rgba(255, 255, 255, 0.9)',
                          }
                        }}
                      >
                        {/* Header con gradiente sutil */}
                        <Box
                          sx={{
                            background: `linear-gradient(135deg, ${GREEN_GRADIENT_START}08 0%, ${GREEN_GRADIENT_MIDDLE}05 100%)`,
                            p: 3,
                            pb: 2
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            mb: 1
                          }}>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 600,
                                fontSize: '1.125rem',
                                lineHeight: 1.2,
                                color: 'rgba(0, 0, 0, 0.87)',
                                letterSpacing: '-0.01em',
                                flexGrow: 1,
                                mr: 2
                              }}
                            >
                              {service.name}
                            </Typography>
                            
                            <Box
                              sx={{
                                background: `linear-gradient(135deg, ${GREEN_GRADIENT_START} 0%, ${GREEN_GRADIENT_MIDDLE} 100%)`,
                                borderRadius: 2,
                                px: 2,
                                py: 0.75,
                                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)',
                                minWidth: 'fit-content'
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 700,
                                  color: 'white',
                                  fontSize: '1rem',
                                  letterSpacing: '-0.01em'
                                }}
                              >
                                ${service.price}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Contenido */}
                        <Box sx={{ p: 3, pt: 1, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          {service.description && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'rgba(0, 0, 0, 0.6)',
                                lineHeight: 1.5,
                                fontSize: '0.875rem',
                                fontWeight: 400,
                                mb: 2.5,
                                flexGrow: 1,
                                letterSpacing: '0.01em'
                              }}
                            >
                              {service.description}
                            </Typography>
                          )}
                          
                          {/* Chips con estilo Apple */}
                          <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 1,
                            mt: 'auto'
                          }}>
                            {service.supportedModes?.inPerson && (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 20,
                                  background: 'rgba(76, 175, 80, 0.08)',
                                  border: '1px solid rgba(76, 175, 80, 0.12)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    background: 'rgba(76, 175, 80, 0.12)',
                                    transform: 'translateY(-1px)'
                                  }
                                }}
                              >
                                <MapPin size={12} style={{ color: GREEN_GRADIENT_MIDDLE }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    color: GREEN_GRADIENT_END,
                                    letterSpacing: '0.02em'
                                  }}
                                >
                                  Presencial
                                </Typography>
                              </Box>
                            )}
                            
                            {service.supportedModes?.homeVisit && (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 20,
                                  background: 'rgba(76, 175, 80, 0.08)',
                                  border: '1px solid rgba(76, 175, 80, 0.12)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    background: 'rgba(76, 175, 80, 0.12)',
                                    transform: 'translateY(-1px)'
                                  }
                                }}
                              >
                                <Home size={12} style={{ color: GREEN_GRADIENT_MIDDLE }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    color: GREEN_GRADIENT_END,
                                    letterSpacing: '0.02em'
                                  }}
                                >
                                  Domicilio
                                </Typography>
                              </Box>
                            )}
                            
                            {service.supportedModes?.online && (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 20,
                                  background: 'rgba(76, 175, 80, 0.08)',
                                  border: '1px solid rgba(76, 175, 80, 0.12)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    background: 'rgba(76, 175, 80, 0.12)',
                                    transform: 'translateY(-1px)'
                                  }
                                }}
                              >
                                <Video size={12} style={{ color: GREEN_GRADIENT_MIDDLE }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    color: GREEN_GRADIENT_END,
                                    letterSpacing: '0.02em'
                                  }}
                                >
                                  Online
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            {profile.description && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {profile.description}
                  </Typography>
                </Box>
              </motion.div>
            )}
          </Paper>
          
          {/* Sección de agendamiento */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 3, md: 4 }, 
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(0,0,0,0.05)'
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Calendar size={24} style={{ marginRight: 12, color: GREEN_GRADIENT_START }} />
                Agendar cita
              </Typography>
              
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                    1. Selecciona una fecha
                  </Typography>
                  
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button
                        onClick={() => setDatePickerOpen(true)}
                        startIcon={<Calendar size={18} />}
                        variant="contained"
                        sx={{
                          background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                          color: '#fff',
                          borderRadius: 2,
                          textTransform: 'none',
                          '&:hover': {
                            background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                          },
                          boxShadow: 'none',
                          width: 'fit-content'
                        }}
                      >
                        Ver fechas
                      </Button>
                  
                      <DatePicker
                        open={datePickerOpen}
                        onClose={() => setDatePickerOpen(false)}
                        onOpen={() => setDatePickerOpen(true)}
                        value={selectedDate}
                        onChange={(newDate) => {
                          setSelectedDate(newDate);
                          setSelectedSlot(null);
                          setDatePickerOpen(false);
                        }}
                        disablePast
                        shouldDisableDate={(date) => {
                          const dayOfWeek = date.getDay();
                          if (!profile.availability.workDays.includes(dayOfWeek)) {
                            return true;
                          }
                      
                          const dateStr = date.toISOString().split('T')[0];
                          const exception = profile.availability.exceptions.find(exc => exc.date === dateStr);
                          if (exception?.isBlocked) {
                            return true;
                          }
                      
                          return false;
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: { display: 'none' }
                          },
                          popper: {
                            placement: isMobile ? 'bottom-start' : 'bottom',
                            modifiers: [
                              {
                                name: 'preventOverflow',
                                options: {
                                  boundary: document.body,
                                  altAxis: true,
                                },
                              }
                            ],
                            sx: {
                              // Estilos para centrar completamente el DatePicker
                              [`@media (min-width: ${theme.breakpoints.values.md}px)`]: {
                                position: 'fixed !important', // Posición fija para control total
                                top: '50% !important',       // Centro vertical
                                left: '50% !important',      // Centro horizontal
                                transform: 'translate(-50%, -50%) !important', // Centrado preciso
                                zIndex: 1300,
                                '& .MuiCalendarPicker-root': {
                                  width: '320px',
                                }
                              }
                            }
                          },
                          desktopPaper: {
                            elevation: 8,
                            sx: {
                              borderRadius: 2,
                              boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
                            }
                          }
                        }}
                      />
                  
                      {selectedDate && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Fecha seleccionada: <strong>{selectedDate.toLocaleDateString()}</strong>
                        </Typography>
                      )}
                  
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Zona horaria: {userTimezone}
                      </Typography>
                    </Box>
                  </LocalizationProvider>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                    2. Selecciona un horario
                  </Typography>
                  
                  {selectedDate ? (
                    availableSlots.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {availableSlots.map((slot, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.2, delay: index * 0.03 }}
                          >
                            <Button
                              variant="contained"
                              size="medium"
                              disableElevation // Elimina la sombra
                              disabled={!slot.available}
                              onClick={() => handleSelectSlot(slot)}
                              sx={{ 
                                minWidth: '90px',
                                borderRadius: 6,
                                py: 1,
                                mb: 1,
                                background: selectedSlot?.time === slot.time 
                                  ? `linear-gradient(to bottom, ${GREEN_GRADIENT_MIDDLE}, ${GREEN_GRADIENT_END})`
                                  : `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                                color: '#fff',
                                '&:hover': {
                                  background: selectedSlot?.time === slot.time 
                                    ? `linear-gradient(to bottom, ${GREEN_GRADIENT_MIDDLE}, ${GREEN_GRADIENT_END})`
                                    : `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                                  boxShadow: 'none' // Asegura que no haya sombra al pasar el mouse
                                },
                                boxShadow: 'none' // Elimina cualquier sombra predeterminada
                              }}
                            >
                              {formatTimeDisplay(slot.time)}
                            </Button>
                          </motion.div>
                        ))}
                      </Box>
                    ) : (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          borderRadius: 2,
                          backgroundColor: 'rgba(76,175,80,0.1)',
                          color: GREEN_GRADIENT_START
                        }}
                      >
                        No hay horarios disponibles para esta fecha.
                      </Alert>
                    )
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Selecciona una fecha para ver los horarios disponibles.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </motion.div>
        </motion.div>
        
        {/* Enlaces para profesionales y centros médicos */}
        <QuickLinks />
      </Container>
      
      {/* Diálogo de reserva */}
      <Dialog 
        open={bookingDialogOpen} 
        onClose={handleCloseBookingDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          pt: 3,
          fontWeight: 600
        }}>
          Agendar cita con {profile.name}
        </DialogTitle>
        
        <DialogContent>
          <Stepper 
            activeStep={activeStep === 0.5 ? 0 : Math.floor(activeStep)} 
            sx={{ 
              mb: 4, 
              mt: 2,
              '& .MuiStepIcon-root.Mui-active': {
                color: GREEN_GRADIENT_START
              },
              '& .MuiStepIcon-root.Mui-completed': {
                color: HEALTH_GREEN
              },
              display: { xs: isMobile ? 'none' : 'flex', sm: 'flex' } // Ocultar en móvil
            }}
          >
            <Step>
              <StepLabel>{!isMobile && "Modalidad"}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{!isMobile && "Datos personales"}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{!isMobile && "Confirmar"}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{!isMobile && "Completado"}</StepLabel>
            </Step>
          </Stepper>
          
          {/* Paso 0: Selección de modalidad */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Selecciona modalidad de atención
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {profile.appointmentModes?.inPerson && (
                  <Button
                    variant={appointmentData.appointmentMode === 'inPerson' ? 'contained' : 'outlined'}
                    onClick={() => setAppointmentData(prev => ({ ...prev, appointmentMode: 'inPerson' }))}
                    startIcon={<MapPin size={18} />}
                    fullWidth
                    sx={{
                      justifyContent: 'flex-start',
                      py: 2,
                      borderRadius: 2,
                      backgroundColor: appointmentData.appointmentMode === 'inPerson' ? 
                        `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})` : 
                        undefined
                    }}
                  >
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="subtitle1">Consulta presencial</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {profile.appointmentLocations?.inPersonAddress || 'En el consultorio del profesional'}
                      </Typography>
                    </Box>
                  </Button>
                )}
                
                {profile.appointmentModes?.homeVisit && (
                  <Button
                    variant={appointmentData.appointmentMode === 'homeVisit' ? 'contained' : 'outlined'}
                    onClick={() => setAppointmentData(prev => ({ ...prev, appointmentMode: 'homeVisit' }))}
                    startIcon={<Home size={18} />}
                    fullWidth
                    sx={{
                      justifyContent: 'flex-start',
                      py: 2,
                      borderRadius: 2,
                      backgroundColor: appointmentData.appointmentMode === 'homeVisit' ? 
                        `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})` : 
                        undefined
                    }}
                  >
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="subtitle1">Atención a domicilio</Typography>
                      <Typography variant="body2" color="text.secondary">
                        El profesional se desplazará a tu domicilio
                      </Typography>
                    </Box>
                  </Button>
                )}
                
                {profile.appointmentModes?.online && (
                  <Button
                    variant={appointmentData.appointmentMode === 'online' ? 'contained' : 'outlined'}
                    onClick={() => setAppointmentData(prev => ({ ...prev, appointmentMode: 'online' }))}
                    startIcon={<Video size={18} />}
                    fullWidth
                    sx={{
                      justifyContent: 'flex-start',
                      py: 2,
                      borderRadius: 2,
                      backgroundColor: appointmentData.appointmentMode === 'online' ? 
                        `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})` : 
                        undefined
                    }}
                  >
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="subtitle1">Consulta online</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Vía {
                          profile.appointmentLocations?.onlinePlatform === 'zoom' ? 'Zoom' :
                          profile.appointmentLocations?.onlinePlatform === 'meet' ? 'Google Meet' :
                          profile.appointmentLocations?.onlinePlatform === 'teams' ? 'Microsoft Teams' :
                          'videollamada'
                        }
                      </Typography>
                    </Box>
                  </Button>
                )}
              </Box>
              
              {/* Campo adicional para dirección en caso de atención a domicilio */}
              {appointmentData.appointmentMode === 'homeVisit' && (
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    label="Dirección completa"
                    value={appointmentData.homeAddress}
                    onChange={(e) => setAppointmentData(prev => ({ 
                      ...prev, 
                      homeAddress: e.target.value 
                    }))}
                    required
                    placeholder="Ingresa tu dirección completa"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: GREEN_GRADIENT_START,
                          borderWidth: 2
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Paso 0.5: Selección de servicio */}
          {activeStep === 0.5 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Selecciona el servicio
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {availableServices && availableServices.length > 0 ? (
                  availableServices
                    // Filtrar servicios activos y compatibles con la modalidad seleccionada
                    .filter(service => 
                      service.isActive && 
                      service.supportedModes && 
                      service.supportedModes[appointmentData.appointmentMode]
                    )
                    .map((service) => (
                      <Button
                        key={service.id}
                        variant={appointmentData.selectedService?.id === service.id ? 'contained' : 'outlined'}
                        onClick={() => handleServiceSelection(service)}
                        fullWidth
                        sx={{
                          justifyContent: 'space-between',
                          py: 2,
                          borderRadius: 2,
                          backgroundColor: appointmentData.selectedService?.id === service.id ? 
                            `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})` : 
                            undefined
                        }}
                      >
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography variant="subtitle1">{service.name}</Typography>
                          {service.description && (
                            <Typography variant="body2" color="text.secondary">
                              {service.description}
                            </Typography>
                          )}
                        </Box>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600,
                            color: appointmentData.selectedService?.id === service.id ? 
                              'white' : GREEN_GRADIENT_START
                          }}
                        >
                          ${service.price}
                        </Typography>
                      </Button>
                    ))
                ) : (
                  <Alert severity="info">
                    Este profesional no ha definido servicios específicos. Continúa para agendar una consulta general.
                  </Alert>
                )}
              </Box>
            </Box>
          )}
          
          {/* Paso 1: Datos personales */}
          {activeStep === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Nombre *
                </Typography>
                <TextField
                  fullWidth
                  name="name"
                  value={patientData.name}
                  onChange={handlePatientDataChange}
                  required
                  placeholder="Ingresa tu nombre"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_GRADIENT_START,
                        borderWidth: 2
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Apellido *
                </Typography>
                <TextField
                  fullWidth
                  name="lastName"
                  value={patientData.lastName}
                  onChange={handlePatientDataChange}
                  required
                  placeholder="Ingresa tu apellido"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_GRADIENT_START,
                        borderWidth: 2
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Correo electrónico
                </Typography>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  value={patientData.email}
                  onChange={handlePatientDataChange}
                  placeholder="Ingresa tu correo"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_GRADIENT_START,
                        borderWidth: 2
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Teléfono *
                </Typography>
                <TextField
                  fullWidth
                  name="phone"
                  value={patientData.phone}
                  onChange={handlePatientDataChange}
                  required
                  placeholder="Ingresa tu teléfono"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_GRADIENT_START,
                        borderWidth: 2
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  RUT o SSN *
                </Typography>
                <TextField
                  fullWidth
                  name="identificationNumber"
                  value={patientData.identificationNumber}
                  onChange={handlePatientDataChange}
                  required
                  placeholder="Ingresa tu RUT"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_GRADIENT_START,
                        borderWidth: 2
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  País *
                </Typography>
                <FormControl fullWidth required>
                  <Select
                    name="countryCode"
                    value={patientData.countryCode || 'cl'} // Preseleccionar Chile
                    onChange={handlePatientDataChange}
                    sx={{
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_GRADIENT_START
                      }
                    }}
                    displayEmpty
                  >
                    <MenuItem value="cl">Chile</MenuItem>
                    <MenuItem value="us">Estados Unidos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Motivo de consulta
                </Typography>
                <TextField
                  fullWidth
                  name="reason"
                  value={patientData.reason}
                  onChange={handlePatientDataChange}
                  multiline
                  rows={3}
                  placeholder="Describe el motivo de tu consulta"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_GRADIENT_START,
                        borderWidth: 2
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          )}
          
          {/* Paso 2: Confirmar */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Resumen de la cita
              </Typography>
              
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  mb: 3, 
                  bgcolor: 'rgba(0,0,0,0.02)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Calendar size={18} style={{ marginRight: 8, color: GREEN_GRADIENT_START }} />
                  <Typography variant="body1">
                    {selectedDate?.toLocaleDateString()}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Clock size={18} style={{ marginRight: 8, color: GREEN_GRADIENT_START }} />
                  <Typography variant="body1">
                    {formatTimeDisplay(selectedSlot?.time)} ({userTimezone})
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Award size={18} style={{ marginRight: 8, color: GREEN_GRADIENT_START }} />
                  <Typography variant="body1">
                    {profile.name} - {profile.title}
                  </Typography>
                </Box>
                
                {/* Mostrar servicio seleccionado */}
                {appointmentData.selectedService && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Tag size={18} style={{ marginRight: 8, color: GREEN_GRADIENT_START }} />
                    <Typography variant="body1">
                      {appointmentData.selectedService.name} - ${appointmentData.selectedService.price}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {appointmentData.appointmentMode === 'inPerson' ? (
                    <MapPin size={18} style={{ marginRight: 8, color: GREEN_GRADIENT_START }} />
                  ) : appointmentData.appointmentMode === 'homeVisit' ? (
                    <Home size={18} style={{ marginRight: 8, color: GREEN_GRADIENT_START }} />
                  ) : (
                    <Video size={18} style={{ marginRight: 8, color: GREEN_GRADIENT_START }} />
                  )}
                  <Typography variant="body1">
                    {appointmentData.appointmentMode === 'inPerson' 
                      ? `Presencial en ${profile.appointmentLocations?.inPersonAddress || 'consultorio'}`
                      : appointmentData.appointmentMode === 'homeVisit' 
                        ? `A domicilio en ${appointmentData.homeAddress}`
                        : `Online vía ${
                            profile.appointmentLocations?.onlinePlatform === 'zoom' ? 'Zoom' :
                            profile.appointmentLocations?.onlinePlatform === 'meet' ? 'Google Meet' :
                            profile.appointmentLocations?.onlinePlatform === 'teams' ? 'Microsoft Teams' :
                            'videollamada'
                          }`
                    }
                  </Typography>
                </Box>
              </Paper>
              
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Datos del paciente
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Nombre completo:</strong> {patientData.name} {patientData.lastName}
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Identificación:</strong> {patientData.identificationNumber}
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>País:</strong> {
                  patientData.countryCode === 'cl' ? 'Chile' :
                  patientData.countryCode === 'us' ? 'Estados Unidos' : patientData.countryCode
                }
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Correo:</strong> {patientData.email || 'No proporcionado'}
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Teléfono:</strong> {patientData.phone}
              </Typography>
              
              {patientData.reason && (
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  <strong>Motivo:</strong> {patientData.reason}
                </Typography>
              )}
              
              {bookingStatus.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {bookingStatus.error}
                </Alert>
              )}
            </Box>
          )}
          
          {/* Paso 3: Completado */}
          {activeStep === 3 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 100 }}
              >
                <CheckCircle size={60} color={HEALTH_GREEN} style={{ marginBottom: 16 }} />
              </motion.div>

              <Typography 
                variant="h5" 
                sx={{ mb: 2, fontWeight: 600, color: HEALTH_GREEN }}
              >
                ¡Cita agendada con éxito!
              </Typography>
              
              <Box sx={{ mb: 3, maxWidth: 450, mx: 'auto' }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 3, 
                    color: 'text.primary',
                    lineHeight: 1.7,
                    fontSize: '1.1rem'
                  }}
                >
                  Hemos enviado la confirmación a tu WhatsApp.
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 2,
                    color: 'text.secondary',
                    lineHeight: 1.7,
                    fontSize: '1rem'
                  }}
                >
                  Para ver el estado de tu cita:
                </Typography>

                <Box sx={{ textAlign: 'left', pl: 2 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 1,
                      color: 'text.primary',
                      lineHeight: 1.6,
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Box component="span" sx={{ mr: 2, color: HEALTH_GREEN, fontWeight: 'bold' }}>•</Box>
                    {'Visita:ㅤ'}
                    <strong>agtapp.com</strong>
                  </Typography>
                  
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 1,
                      color: 'text.primary',
                      lineHeight: 1.6,
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Box component="span" sx={{ mr: 2, color: HEALTH_GREEN, fontWeight: 'bold' }}>•</Box>
                    {'Selecciona la app:ㅤ'}
                    <strong>AGT Health</strong>
                  </Typography>
                  
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'text.primary',
                      lineHeight: 1.6,
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Box component="span" sx={{ mr: 2, color: HEALTH_GREEN, fontWeight: 'bold' }}>•</Box>
                    Ingresa tus datos
                  </Typography>
                </Box>
              </Box>

              <Button 
                variant="contained"
                disableElevation
                onClick={() => window.open('https://agtapp.com', '_blank')}
                sx={{ 
                  borderRadius: 28,
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                  color: '#fff',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  '&:hover': {
                    background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                    transform: 'translateY(-1px)',
                    boxShadow: 'none'
                  },
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: 'none'
                }}
              >
                Visitar AGT
              </Button>

              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  borderRadius: 3, 
                  mt: 4, 
                  textAlign: 'left',
                  bgcolor: 'rgba(52,199,89,0.08)',
                  border: '1px solid rgba(52,199,89,0.12)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 2, 
                    color: HEALTH_GREEN, 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.75rem'
                  }}
                >
                  Detalles de la cita
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Calendar size={18} style={{ marginRight: 12, color: HEALTH_GREEN }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedDate?.toLocaleDateString('es-ES', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Clock size={18} style={{ marginRight: 12, color: HEALTH_GREEN }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatTimeDisplay(selectedSlot?.time)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Award size={18} style={{ marginRight: 12, color: HEALTH_GREEN }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {profile.name} - {profile.title}
                  </Typography>
                </Box>
                
                {/* Mostrar servicio seleccionado */}
                {appointmentData.selectedService && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Tag size={18} style={{ marginRight: 12, color: HEALTH_GREEN }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {appointmentData.selectedService.name} - ${appointmentData.selectedService.price}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  {appointmentData.appointmentMode === 'inPerson' ? (
                    <MapPin size={18} style={{ marginRight: 12, color: HEALTH_GREEN }} />
                  ) : appointmentData.appointmentMode === 'homeVisit' ? (
                    <Home size={18} style={{ marginRight: 12, color: HEALTH_GREEN }} />
                  ) : (
                    <Video size={18} style={{ marginRight: 12, color: HEALTH_GREEN }} />
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {appointmentData.appointmentMode === 'inPerson' 
                      ? `Presencial en ${profile.appointmentLocations?.inPersonAddress || 'consultorio'}`
                      : appointmentData.appointmentMode === 'homeVisit' 
                        ? `A domicilio en ${appointmentData.homeAddress}`
                        : `Online vía ${
                            profile.appointmentLocations?.onlinePlatform === 'zoom' ? 'Zoom' :
                            profile.appointmentLocations?.onlinePlatform === 'meet' ? 'Google Meet' :
                            profile.appointmentLocations?.onlinePlatform === 'teams' ? 'Microsoft Teams' :
                            'videollamada'
                          }`
                    }
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {/* Botones para paso 0: Selección de modalidad */}
          {activeStep === 0 && (
            <>
              <Button 
                onClick={handleCloseBookingDialog} 
                startIcon={<X size={16} />}
                sx={{ 
                  textTransform: 'none',
                  color: 'text.secondary'
                }}
              >
                Cancelar
              </Button>
              <Button 
                variant="contained" 
                onClick={handleNext}
                disableElevation
                disabled={!appointmentData.appointmentMode || 
                         (appointmentData.appointmentMode === 'homeVisit' && !appointmentData.homeAddress)}
                sx={{ 
                  background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                  '&:hover': {
                    background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                    boxShadow: 'none'
                  },
                  textTransform: 'none',
                  borderRadius: 28,
                  px: 3,
                  boxShadow: 'none'
                }}
              >
                Continuar
              </Button>
            </>
          )}
          
          {/* Botones para paso 0.5: Selección de servicio */}
          {activeStep === 0.5 && (
            <>
              <Button 
                onClick={handleBack}
                sx={{ 
                  textTransform: 'none',
                  color: 'text.secondary'
                }}
              >
                Atrás
              </Button>
              <Button 
                variant="contained" 
                onClick={handleNext}
                disableElevation
                disabled={availableServices.length > 0 && !appointmentData.selectedService}
                sx={{ 
                  background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                  '&:hover': {
                    background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                    boxShadow: 'none'
                  },
                  textTransform: 'none',
                  borderRadius: 28,
                  px: 3,
                  boxShadow: 'none'
                }}
              >
                Continuar
              </Button>
            </>
          )}
          
          {/* Botones para paso 1: Datos personales */}
          {activeStep === 1 && (
            <>
              <Button 
                onClick={handleBack}
                sx={{ 
                  textTransform: 'none',
                  color: 'text.secondary'
                }}
              >
                Atrás
              </Button>
              <Button 
                variant="contained" 
                onClick={handleNext}
                disableElevation
                disabled={!patientData.name || !patientData.lastName || !patientData.phone || !patientData.identificationNumber || !patientData.countryCode}
                sx={{ 
                  background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                  '&:hover': {
                    background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                    boxShadow: 'none'
                  },
                  textTransform: 'none',
                  borderRadius: 28,
                  px: 3,
                  boxShadow: 'none'
                }}
              >
                Continuar
              </Button>
            </>
          )}
          
          {/* Botones para paso 2: Confirmar */}
          {activeStep === 2 && (
            <>
              <Button 
                onClick={handleBack}
                disabled={bookingStatus.loading}
                sx={{ 
                  textTransform: 'none',
                  color: 'text.secondary'
                }}
              >
                Atrás
              </Button>
              <Button 
                variant="contained" 
                onClick={handleConfirmBooking}
                disableElevation
                disabled={bookingStatus.loading}
                sx={{ 
                  background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                  '&:hover': {
                    background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                    boxShadow: 'none'
                  },
                  textTransform: 'none',
                  borderRadius: 28,
                  px: 3,
                  boxShadow: 'none'
                }}
              >
                {bookingStatus.loading ? <CircularProgress size={24} color="inherit" /> : 'Confirmar cita'}
              </Button>
            </>
          )}
          
          {/* Botones para paso 3: Completado */}
          {activeStep === 3 && (
            <Button 
              variant="contained" 
              onClick={handleCloseBookingDialog}
              disableElevation
              sx={{ 
                background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                '&:hover': {
                  background: `linear-gradient(to bottom, ${GREEN_GRADIENT_START}, ${GREEN_GRADIENT_END})`,
                  boxShadow: 'none'
                },
                textTransform: 'none',
                borderRadius: 28,
                px: 3,
                boxShadow: 'none'
              }}
            >
              Cerrar
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Visor de historias */}
      {stories.length > 0 && (
        <StoryViewer
          stories={stories}
          open={storyViewerOpen}
          onClose={() => setStoryViewerOpen(false)}
          professionalName={profile.name}
          professionalImage={profile.profileImageUrl}
        />
      )}
      
      <Footer />
    </>
  );
};

export default ProfessionalPublicProfile;